import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import toast from "react-hot-toast";

import StepProgress from "../components/website-Generator/StepProgress";
import BusinessDetailsStep from "../components/website-Generator/BusinessDetailsStep";
import AiGeneratorAssistant from "../components/website-Generator/AiGeneratorAssistant";
import ThemeSelectionStep from "../components/website-Generator/ThemeSelectionStep";
import ReviewStep from "../components/website-Generator/ReviewStep";
import ActionBar from "../components/website-Generator/ActionBar";
import GeneratorLoadingCard from "../components/website-Generator/GeneratorLoadingCard";
import GeneratorThinkingPanel from "../components/website-Generator/GeneratorThinkingPanel";
import GeneratorInstantPreview from "../components/website-Generator/GeneratorInstantPreview";
import GeneratorHeroPreview from "../components/website-Generator/GeneratorHeroPreview";
import SmartSuggestionsSummaryPanel from "../components/website-Generator/SmartSuggestionsSummaryPanel";
import SuggestionsRefreshNotice from "../components/website-Generator/SuggestionsRefreshNotice";
import GeneratorFooter from "../components/website-Generator/GeneratorFooter";
import QualityCard from "../components/website-Generator/QualityCard";
import SmartRegenerateActions from "../components/website-Generator/SmartRegenerateActions";
import PublishGuardrailCard from "../components/website-Generator/PublishGuardrailCard";
import MobileGeneratorComposer from "../components/website-Generator/MobileGeneratorComposer";
import MobileGeneratorChatFlow from "../components/website-Generator/MobileGeneratorChatFlow";
import MobilePreviewReadyCard from "../components/website-Generator/MobilePreviewReadyCard";

import {
    createAndQueueProject,
    createAndQueuePreviewSession,
    getPreview,
    getPreviewSession,
    getPreviewSessionStatus,
    claimPreviewSession,
    publishProject,
    regenerateProject,
    autofillWebsiteForm,
    getThemes
} from "../services/websiteService";

import {
    getCurrentUser,
    redirectToLogin,
    redirectToSignup
} from "../services/authService";

import "./LandingPage.css";
import "./GenerateWebsitePage.css";

const fallbackThemes = [
    { key: "clean-light", name: "Clean Light", description: "Modern clean business layout" },
    { key: "modern-glass", name: "Modern Glass", description: "Glassmorphism futuristic UI" },
    { key: "premium-dark", name: "Premium Dark", description: "Dark luxury professional layout" },
    { key: "luxury-light", name: "Luxury Light", description: "Elegant light premium feel" },
    { key: "bold-gradient", name: "Bold Gradient", description: "Vibrant modern tech UI" }
];

const defaultRecommendedPages = ["Home", "About", "Services", "Contact"];

const AI_LOCKABLE_FIELDS = new Set([
    "subIndustry",
    "businessModel",
    "primaryWebsiteMode",
    "primaryEntityType",
    "locationFocus",
    "contentStyleHint",
    "themeKey",
    "tone",
    "brandStyle",
    "pricePositioning",
    "callToActionType",
    "pages"
]);

function createEmptyOfferedItem() {
    return {
        id: crypto.randomUUID(),
        name: "",
        subtitle: "",
        description: "",
        badge: "",
        category: "",
        audience: "",
        level: "",
        imagePreference: "",
        keyPoints: [""]
    };
}

function normalizeOfferedItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return [createEmptyOfferedItem()];
    }

    return items.map((item) => ({
        id: item.id || crypto.randomUUID(),
        name: item.name || "",
        subtitle: item.subtitle || "",
        description: item.description || "",
        badge: item.badge || "",
        category: item.category || "",
        audience: item.audience || "",
        level: item.level || "",
        imagePreference: item.imagePreference || "",
        keyPoints: Array.isArray(item.keyPoints) && item.keyPoints.length > 0
            ? item.keyPoints.map((x) => x || "")
            : [""]
    }));
}

function parseJsonSafe(value, fallback = null) {
    if (!value) return fallback;
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

const isProjectGenerating = (project) => {
    const status = String(
        project?.status ||
        project?.generationStatus ||
        project?.previewStatus ||
        ""
    ).toLowerCase();

    return (
        status.includes("generating") ||
        status.includes("submitted") ||
        status.includes("pending") ||
        status.includes("queued") ||
        status.includes("processing")
    );
};

function detectMobileGeneratorViewport() {
    if (typeof window === "undefined") return false;

    const widths = [
        window.innerWidth,
        window.visualViewport?.width,
        document.documentElement?.clientWidth,
        window.screen?.width,
        window.screen?.availWidth
    ]
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

    const smallestWidth = widths.length > 0
        ? Math.min(...widths)
        : 9999;

    const isTouchLike =
        typeof navigator !== "undefined" &&
        (
            navigator.maxTouchPoints > 0 ||
            navigator.userAgentData?.mobile === true ||
            /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || "")
        );

    return smallestWidth <= 768 || (isTouchLike && smallestWidth <= 1024);
}

export default function GenerateWebsitePage() {
    const location = useLocation();

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const queryBusinessName = queryParams.get("name") || "";
    const queryIndustry = queryParams.get("industry") || "";
    const queryPreviewSessionId = queryParams.get("previewSessionId") || "";

    const PREVIEW_STORAGE_KEY = "pending_preview_session_id";

    const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "https://api.shrianshnexus.in";

    const previewSeed = useMemo(() => {
        if (location.state?.previewSeed) return location.state.previewSeed;

        if (!queryBusinessName && !queryIndustry) return null;

        return {
            businessName: queryBusinessName,
            industry: queryIndustry,
            autoGenerate: true
        };
    }, [location.state, queryBusinessName, queryIndustry]);

    const [currentUser, setCurrentUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [ownershipMode, setOwnershipMode] = useState("guest");

    const [generatorMode, setGeneratorMode] = useState("assistant");
    const [formMode, setFormMode] = useState("fast");
    const [step, setStep] = useState(1);
    const [mobileComposerStarted, setMobileComposerStarted] = useState(false);
    const [mobileChatStarted, setMobileChatStarted] = useState(false);
    const [isMobileGenerator, setIsMobileGenerator] = useState(() =>
        detectMobileGeneratorViewport()
    );

    const [themes, setThemes] = useState(fallbackThemes);

    const [loading, setLoading] = useState(false);
    const [autofilling, setAutofilling] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    const [projectId, setProjectId] = useState("");
    const [previewSessionId, setPreviewSessionId] = useState("");
    const [status, setStatus] = useState("");
    const [liveUrl, setLiveUrl] = useState("");
    const [previewData, setPreviewData] = useState(null);

    const [selectedVariant, setSelectedVariant] = useState("A");
    const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
    const [previewError, setPreviewError] = useState("");

    const [showOfferings, setShowOfferings] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showAiSummary, setShowAiSummary] = useState(false);
    const [showHeroPreview, setShowHeroPreview] = useState(false);

    const [fieldSourceMap, setFieldSourceMap] = useState({});
    const [lockedAiFields, setLockedAiFields] = useState({});
    const [lastAutofillSignature, setLastAutofillSignature] = useState("");
    const [needsSuggestionRefresh, setNeedsSuggestionRefresh] = useState(false);
    const [autoPreviewTriggered, setAutoPreviewTriggered] = useState(false);
    const [autoAutofillTriggered, setAutoAutofillTriggered] = useState(false);
    const [previewRestoreChecked, setPreviewRestoreChecked] = useState(false);

    const [claimingPreview, setClaimingPreview] = useState(false);
    const autoClaimAttemptedRef = useRef(false);
    const lastHydratedPreviewKeyRef = useRef("");

    const authReturnUrl = encodeURIComponent(window.location.href);
    const loginUrl = `https://admin.shrianshnexus.in/login?entry=public&returnUrl=${authReturnUrl}`;
    const signupUrl = `https://admin.shrianshnexus.in/signup?entry=public&returnUrl=${authReturnUrl}`;

    useEffect(() => {
        const checkMobile = () => {
            setIsMobileGenerator(detectMobileGeneratorViewport());
        };

        checkMobile();

        window.addEventListener("resize", checkMobile);
        window.addEventListener("orientationchange", checkMobile);

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", checkMobile);
        }

        return () => {
            window.removeEventListener("resize", checkMobile);
            window.removeEventListener("orientationchange", checkMobile);

            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", checkMobile);
            }
        };
    }, []);

    const [form, setForm] = useState({
        businessName: "",
        industry: "",
        subIndustry: "",
        description: "",

        tone: "Professional",
        themeKey: "",

        visualDirection: "",
        imageStrategy: "",
        crmModules: [],

        primaryAudience: "",
        secondaryAudience: "",
        location: "",
        serviceAreas: "",

        goalOfWebsite: "",
        callToActionType: "",
        brandStyle: "",
        pricePositioning: "",

        coreServices: "",
        projectTypes: "",
        uniqueSellingPoints: "",
        trustSignals: "",
        yearsInBusiness: "",
        mustIncludeWords: "",
        avoidWords: "",

        competitorWebsiteUrl: "",
        competitorStyleNotes: "",
        desiredLayoutStyle: "",
        visualTone: "",
        preferredSectionDensity: "",
        referenceWebsiteType: "",

        businessModel: "",
        primaryWebsiteMode: "",
        primaryEntityType: "",
        locationFocus: "",
        contentStyleHint: "",

        pages: defaultRecommendedPages,
        offeredItems: [createEmptyOfferedItem()]
    });

    const previewDocumentUrl = useMemo(() => {
        if (projectId) {
            return `${API_BASE_URL}/api/website/${projectId}/preview-document?v=${selectedVariant}&r=${previewRefreshKey || Date.now()}`;
        }

        if (previewSessionId) {
            return `${API_BASE_URL}/api/website/preview-session/${previewSessionId}/document?v=${selectedVariant}&r=${previewRefreshKey || Date.now()}`;
        }

        return "";
    }, [API_BASE_URL, projectId, previewSessionId, selectedVariant, previewRefreshKey]);

    const persistPreviewSessionId = useCallback((id) => {
        if (!id) return;

        try {
            localStorage.setItem(PREVIEW_STORAGE_KEY, id);
        } catch {
            // ignore
        }

        const url = new URL(window.location.href);
        url.searchParams.set("previewSessionId", id);
        window.history.replaceState({}, "", url.toString());
    }, []);

    const clearPersistedPreviewSessionId = useCallback(() => {
        try {
            localStorage.removeItem(PREVIEW_STORAGE_KEY);
        } catch {
            // ignore
        }

        const url = new URL(window.location.href);
        url.searchParams.delete("previewSessionId");
        window.history.replaceState({}, "", url.toString());
    }, []);

    const getPendingPreviewSessionId = useCallback(() => {
        if (queryPreviewSessionId) return queryPreviewSessionId;

        try {
            return localStorage.getItem(PREVIEW_STORAGE_KEY) || "";
        } catch {
            return "";
        }
    }, [queryPreviewSessionId]);

    const resetHydrationState = useCallback(() => {
        lastHydratedPreviewKeyRef.current = "";
    }, []);

    const refreshPreview = useCallback(() => {
        setPreviewRefreshKey((prev) => prev + 1);
    }, []);

    const markFieldAsManual = useCallback((field) => {
        setFieldSourceMap((prev) => ({
            ...prev,
            [field]: "manual"
        }));
    }, []);

    const markFieldsAsAiSuggested = useCallback((fields) => {
        setFieldSourceMap((prev) => {
            const next = { ...prev };

            fields.forEach((field) => {
                next[field] = "ai";
            });

            return next;
        });
    }, []);

    const updateField = useCallback((name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));

        markFieldAsManual(name);

        const coreResetFields = new Set([
            "businessName",
            "industry",
            "subIndustry",
            "description",
            "goalOfWebsite"
        ]);

        if (coreResetFields.has(name)) {
            if (previewSessionId && !projectId) {
                setPreviewSessionId("");
                setPreviewData(null);
                setStatus("");
                clearPersistedPreviewSessionId();
                resetHydrationState();
                autoClaimAttemptedRef.current = false;
            }
        }

        if (AI_LOCKABLE_FIELDS.has(name)) {
            setLockedAiFields((prev) => ({
                ...prev,
                [name]: true
            }));
        }
    }, [
        markFieldAsManual,
        previewSessionId,
        projectId,
        clearPersistedPreviewSessionId,
        resetHydrationState
    ]);

    const toggleAiFieldLock = useCallback((field) => {
        setLockedAiFields((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    }, []);

    const buildAutofillPayload = useCallback(() => {
        const normalizedOfferedItems = Array.isArray(form.offeredItems)
            ? form.offeredItems
                .map((item) => ({
                    name: item.name?.trim() || "",
                    subtitle: item.subtitle?.trim() || "",
                    description: item.description?.trim() || "",
                    badge: item.badge?.trim() || "",
                    category: item.category?.trim() || "",
                    audience: item.audience?.trim() || "",
                    level: item.level?.trim() || "",
                    imagePreference: item.imagePreference?.trim() || "",
                    keyPoints: Array.isArray(item.keyPoints)
                        ? item.keyPoints.map((x) => x?.trim() || "").filter(Boolean)
                        : []
                }))
                .filter((item) =>
                    item.name ||
                    item.subtitle ||
                    item.description ||
                    item.badge ||
                    item.category ||
                    item.audience ||
                    item.level ||
                    item.imagePreference ||
                    item.keyPoints.length > 0
                )
            : [];

        return {
            businessName: form.businessName?.trim() || "",
            industry: form.industry?.trim() || "",
            subIndustry: form.subIndustry?.trim() || "",
            description: form.description?.trim() || "",
            coreServices: form.coreServices?.trim() || "",
            projectTypes: form.projectTypes?.trim() || "",
            goalOfWebsite: form.goalOfWebsite?.trim() || "",
            primaryAudience: form.primaryAudience?.trim() || "",
            offeredItems: normalizedOfferedItems,

            businessModel: "",
            primaryWebsiteMode: "",
            primaryEntityType: "",
            locationFocus: "",
            contentStyleHint: ""
        };
    }, [form]);

    const buildCreatePayload = useCallback(() => ({
        businessName: form.businessName,
        industry: form.industry,
        subIndustry: form.subIndustry,
        description: form.description,

        tone: form.tone,
        themeKey: form.themeKey,

        visualDirection: form.visualDirection,
        imageStrategy: form.imageStrategy,
        crmModules: form.crmModules,

        primaryAudience: form.primaryAudience,
        secondaryAudience: form.secondaryAudience,
        location: form.location,
        serviceAreas: form.serviceAreas,

        goalOfWebsite: form.goalOfWebsite,
        callToActionType: form.callToActionType,
        brandStyle: form.brandStyle,
        pricePositioning: form.pricePositioning,

        coreServices: form.coreServices,
        projectTypes: form.projectTypes,
        uniqueSellingPoints: form.uniqueSellingPoints,
        trustSignals: form.trustSignals,
        yearsInBusiness: form.yearsInBusiness,
        mustIncludeWords: form.mustIncludeWords,
        avoidWords: form.avoidWords,

        competitorWebsiteUrl: form.competitorWebsiteUrl,
        competitorStyleNotes: form.competitorStyleNotes,
        desiredLayoutStyle: form.desiredLayoutStyle,
        visualTone: form.visualTone,
        preferredSectionDensity: form.preferredSectionDensity,
        referenceWebsiteType: form.referenceWebsiteType,

        businessModel: form.businessModel,
        primaryWebsiteMode: form.primaryWebsiteMode,
        primaryEntityType: form.primaryEntityType,
        locationFocus: form.locationFocus,
        contentStyleHint: form.contentStyleHint,

        pages: Array.isArray(form.pages)
            ? form.pages.map((p) => String(p).toLowerCase())
            : [],

        offeredItems: Array.isArray(form.offeredItems)
            ? form.offeredItems.map((item) => ({
                name: item.name,
                subtitle: item.subtitle,
                description: item.description,
                badge: item.badge,
                category: item.category,
                audience: item.audience,
                level: item.level,
                imagePreference: item.imagePreference,
                keyPoints: Array.isArray(item.keyPoints)
                    ? item.keyPoints.filter(Boolean)
                    : []
            }))
            : []
    }), [form]);

    const buildAutofillSignature = useCallback(() => {
        const payload = buildAutofillPayload();

        return JSON.stringify({
            businessName: payload.businessName,
            industry: payload.industry,
            subIndustry: payload.subIndustry,
            description: payload.description,
            coreServices: payload.coreServices,
            projectTypes: payload.projectTypes,
            goalOfWebsite: payload.goalOfWebsite,
            primaryAudience: payload.primaryAudience,
            offeredItems: payload.offeredItems
        });
    }, [buildAutofillPayload]);

    const mergeValue = (prevValue, aiValue) => {
        return prevValue && String(prevValue).trim() !== ""
            ? prevValue
            : (aiValue || prevValue);
    };

    const hasMeaningfulOfferedItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return false;

        return items.some((item) =>
            item?.name?.trim() ||
            item?.subtitle?.trim() ||
            item?.description?.trim() ||
            item?.badge?.trim() ||
            item?.category?.trim() ||
            item?.audience?.trim() ||
            item?.level?.trim() ||
            item?.imagePreference?.trim() ||
            (Array.isArray(item?.keyPoints) && item.keyPoints.some((x) => x?.trim()))
        );
    };

    const mergeAutofillData = useCallback((data) => {
        setForm((prev) => {
            const userHasOfferedItems = hasMeaningfulOfferedItems(prev.offeredItems);

            const themeIsValid =
                !data?.themeKey ||
                themes.some((t) => t.key === data.themeKey);

            const keepIfLocked = (field, incomingValue, currentValue) => {
                if (lockedAiFields[field]) return currentValue;
                return mergeValue(currentValue, incomingValue);
            };

            return {
                ...prev,

                industry: prev.industry,
                subIndustry: keepIfLocked("subIndustry", data?.subIndustry, prev.subIndustry),

                businessModel: keepIfLocked("businessModel", data?.businessModel, prev.businessModel),
                primaryWebsiteMode: keepIfLocked("primaryWebsiteMode", data?.primaryWebsiteMode, prev.primaryWebsiteMode),
                primaryEntityType: keepIfLocked("primaryEntityType", data?.primaryEntityType, prev.primaryEntityType),
                locationFocus: keepIfLocked("locationFocus", data?.locationFocus, prev.locationFocus),
                contentStyleHint: keepIfLocked("contentStyleHint", data?.contentStyleHint, prev.contentStyleHint),

                pages: lockedAiFields.pages
                    ? prev.pages
                    : Array.isArray(data?.pages) && data.pages.length > 0
                        ? data.pages
                        : prev.pages,

                businessName: mergeValue(prev.businessName, data?.businessName),
                description: mergeValue(prev.description, data?.description),

                tone: keepIfLocked("tone", data?.tone, prev.tone),
                themeKey: lockedAiFields.themeKey
                    ? prev.themeKey
                    : themeIsValid
                        ? mergeValue(prev.themeKey, data?.themeKey)
                        : prev.themeKey,

                primaryAudience: mergeValue(prev.primaryAudience, data?.primaryAudience),
                secondaryAudience: mergeValue(prev.secondaryAudience, data?.secondaryAudience),
                location: mergeValue(prev.location, data?.location),
                serviceAreas: mergeValue(prev.serviceAreas, data?.serviceAreas),

                goalOfWebsite: mergeValue(prev.goalOfWebsite, data?.goalOfWebsite),
                callToActionType: keepIfLocked("callToActionType", data?.callToActionType, prev.callToActionType),
                brandStyle: keepIfLocked("brandStyle", data?.brandStyle, prev.brandStyle),
                pricePositioning: keepIfLocked("pricePositioning", data?.pricePositioning, prev.pricePositioning),

                coreServices: mergeValue(prev.coreServices, data?.coreServices),
                projectTypes: mergeValue(prev.projectTypes, data?.projectTypes),
                uniqueSellingPoints: mergeValue(prev.uniqueSellingPoints, data?.uniqueSellingPoints),
                trustSignals: mergeValue(prev.trustSignals, data?.trustSignals),
                yearsInBusiness: mergeValue(prev.yearsInBusiness, data?.yearsInBusiness),
                mustIncludeWords: mergeValue(prev.mustIncludeWords, data?.mustIncludeWords),
                avoidWords: mergeValue(prev.avoidWords, data?.avoidWords),

                competitorWebsiteUrl: mergeValue(prev.competitorWebsiteUrl, data?.competitorWebsiteUrl),
                competitorStyleNotes: mergeValue(prev.competitorStyleNotes, data?.competitorStyleNotes),
                desiredLayoutStyle: mergeValue(prev.desiredLayoutStyle, data?.desiredLayoutStyle),
                visualTone: mergeValue(prev.visualTone, data?.visualTone),
                preferredSectionDensity: mergeValue(prev.preferredSectionDensity, data?.preferredSectionDensity),
                referenceWebsiteType: mergeValue(prev.referenceWebsiteType, data?.referenceWebsiteType),

                visualDirection: data?.visualDirection || prev.visualDirection || "",
                imageStrategy: data?.imageStrategy || prev.imageStrategy || "",
                crmModules: Array.isArray(data?.crmModules) ? data.crmModules : (prev.crmModules || []),

                offeredItems: userHasOfferedItems
                    ? prev.offeredItems
                    : Array.isArray(data?.offeredItems) && data.offeredItems.length > 0
                        ? normalizeOfferedItems(data.offeredItems)
                        : prev.offeredItems
            };
        });
    }, [themes, lockedAiFields]);

    const handleAutofill = useCallback(async () => {
        const payload = buildAutofillPayload();

        if (!payload.businessName || !payload.industry || !payload.description || !payload.goalOfWebsite) {
            return;
        }

        try {
            setAutofilling(true);

            const data = await autofillWebsiteForm(payload);
            mergeAutofillData(data);

            markFieldsAsAiSuggested([
                "subIndustry",
                "businessModel",
                "primaryWebsiteMode",
                "primaryEntityType",
                "locationFocus",
                "contentStyleHint",
                "themeKey",
                "visualDirection",
                "imageStrategy",
                "crmModules",
                "tone",
                "brandStyle",
                "pricePositioning",
                "callToActionType",
                "primaryAudience",
                "secondaryAudience",
                "serviceAreas",
                "coreServices",
                "projectTypes",
                "uniqueSellingPoints",
                "trustSignals",
                "yearsInBusiness",
                "mustIncludeWords",
                "avoidWords",
                "competitorWebsiteUrl",
                "competitorStyleNotes",
                "desiredLayoutStyle",
                "visualTone",
                "preferredSectionDensity",
                "referenceWebsiteType",
                "goalOfWebsite"
            ]);

            if (Array.isArray(data?.offeredItems) && data.offeredItems.length > 0) {
                setShowOfferings(true);
            }

            setLastAutofillSignature(buildAutofillSignature());
            setNeedsSuggestionRefresh(false);
        } catch (error) {
            console.error(error);
            toast.error("AI suggestions could not be prepared.");
        } finally {
            setAutofilling(false);
        }
    }, [
        buildAutofillPayload,
        mergeAutofillData,
        markFieldsAsAiSuggested,
        buildAutofillSignature
    ]);

    const hydrateFormFromPreview = useCallback((preview) => {
        if (!preview) return;

        const previewKey =
            preview.projectId ||
            preview.previewSessionId ||
            preview.id ||
            "";

        if (previewKey && lastHydratedPreviewKeyRef.current === previewKey) {
            return;
        }

        const request = preview.request || parseJsonSafe(preview.requestJson, {}) || {};
        const offeredItems =
            parseJsonSafe(preview.offeredItemsJson, null) ||
            request.offeredItems ||
            [];

        const pagesFromPreview =
            Array.isArray(preview.pages) && preview.pages.length > 0
                ? preview.pages.map((p) => p.title || p.key || "").filter(Boolean)
                : Array.isArray(request.pages) && request.pages.length > 0
                    ? request.pages
                    : defaultRecommendedPages;

        setForm((prev) => ({
            ...prev,
            businessName: preview.businessName || request.businessName || prev.businessName || "",
            industry: preview.industry || request.industry || prev.industry || "",
            subIndustry: preview.subIndustry || request.subIndustry || prev.subIndustry || "",
            description: preview.description || request.description || prev.description || "",

            tone: preview.tone || request.tone || prev.tone || "Professional",
            themeKey: preview.themeKey || request.themeKey || prev.themeKey || "",

            primaryAudience: preview.primaryAudience || request.primaryAudience || prev.primaryAudience || "",
            secondaryAudience: preview.secondaryAudience || request.secondaryAudience || prev.secondaryAudience || "",
            location: preview.location || request.location || prev.location || "",
            serviceAreas: preview.serviceAreas || request.serviceAreas || prev.serviceAreas || "",

            goalOfWebsite: preview.goalOfWebsite || request.goalOfWebsite || prev.goalOfWebsite || "",
            callToActionType: preview.callToActionType || request.callToActionType || prev.callToActionType || "",
            brandStyle: preview.brandStyle || request.brandStyle || prev.brandStyle || "",
            pricePositioning: preview.pricePositioning || request.pricePositioning || prev.pricePositioning || "",

            coreServices: preview.coreServices || request.coreServices || prev.coreServices || "",
            projectTypes: preview.projectTypes || request.projectTypes || prev.projectTypes || "",
            uniqueSellingPoints: preview.uniqueSellingPoints || request.uniqueSellingPoints || prev.uniqueSellingPoints || "",
            trustSignals: preview.trustSignals || request.trustSignals || prev.trustSignals || "",
            yearsInBusiness: preview.yearsInBusiness || request.yearsInBusiness || prev.yearsInBusiness || "",
            mustIncludeWords: preview.mustIncludeWords || request.mustIncludeWords || prev.mustIncludeWords || "",
            avoidWords: preview.avoidWords || request.avoidWords || prev.avoidWords || "",

            competitorWebsiteUrl: preview.competitorWebsiteUrl || request.competitorWebsiteUrl || prev.competitorWebsiteUrl || "",
            competitorStyleNotes: preview.competitorStyleNotes || request.competitorStyleNotes || prev.competitorStyleNotes || "",
            desiredLayoutStyle: preview.desiredLayoutStyle || request.desiredLayoutStyle || prev.desiredLayoutStyle || "",
            visualTone: preview.visualTone || request.visualTone || prev.visualTone || "",
            preferredSectionDensity: preview.preferredSectionDensity || request.preferredSectionDensity || prev.preferredSectionDensity || "",
            referenceWebsiteType: preview.referenceWebsiteType || request.referenceWebsiteType || prev.referenceWebsiteType || "",

            businessModel: preview.businessModel || request.businessModel || prev.businessModel || "",
            primaryWebsiteMode: preview.primaryWebsiteMode || request.primaryWebsiteMode || prev.primaryWebsiteMode || "",
            primaryEntityType: preview.primaryEntityType || request.primaryEntityType || prev.primaryEntityType || "",
            locationFocus: preview.locationFocus || request.locationFocus || prev.locationFocus || "",
            contentStyleHint: preview.contentStyleHint || request.contentStyleHint || prev.contentStyleHint || "",

            visualDirection: preview.visualDirection || request.visualDirection || prev.visualDirection || "",
            imageStrategy: preview.imageStrategy || request.imageStrategy || prev.imageStrategy || "",
            crmModules: Array.isArray(preview.crmModules)
                ? preview.crmModules
                : Array.isArray(request.crmModules)
                    ? request.crmModules
                    : prev.crmModules,

            pages: Array.isArray(pagesFromPreview) && pagesFromPreview.length > 0
                ? pagesFromPreview
                : prev.pages,

            offeredItems: normalizeOfferedItems(offeredItems)
        }));

        if (previewKey) {
            lastHydratedPreviewKeyRef.current = previewKey;
        }
    }, []);

    const loadProjectPreview = useCallback(async (id) => {
        const res = await getPreview(id);

        setPreviewData(res || null);
        setStatus(res?.status || "");
        setLiveUrl(res?.liveUrl || "");

        return res;
    }, []);

    const loadGuestPreview = useCallback(async (id) => {
        const res = await getPreviewSession(id);

        setPreviewData(res || null);
        setStatus(res?.status || "");
        setLiveUrl("");

        return res;
    }, []);

    const pollProjectStatus = useCallback((id) => {
        const interval = setInterval(async () => {
            try {
                const preview = await loadProjectPreview(id);

                if (preview?.status === "Generated") {
                    clearInterval(interval);
                    setPreviewError("");
                    setPreviewData(preview || null);
                    setStatus("Generated");
                    hydrateFormFromPreview(preview);
                    refreshPreview();
                    toast.success("Website generated.");
                }

                if (preview?.status === "Failed") {
                    clearInterval(interval);
                    setPreviewError("Website generation failed.");
                    toast.error("Website generation failed.");
                }
            } catch (error) {
                clearInterval(interval);
                console.error(error);
                setPreviewError("Preview is not available yet.");
            }
        }, 3000);

        return interval;
    }, [loadProjectPreview, hydrateFormFromPreview, refreshPreview]);

    const pollPreviewSessionStatus = useCallback((id) => {
        const interval = setInterval(async () => {
            try {
                const statusRes = await getPreviewSessionStatus(id);
                setStatus(statusRes?.status || "");

                if (statusRes?.status === "Generated") {
                    const preview = await loadGuestPreview(id);
                    clearInterval(interval);
                    setPreviewError("");
                    setPreviewData(preview || null);
                    setStatus("Generated");
                    hydrateFormFromPreview(preview);
                    refreshPreview();
                    toast.success("Preview generated.");
                }

                if (statusRes?.status === "Failed") {
                    clearInterval(interval);
                    setPreviewError("Website preview generation failed.");
                    toast.error("Website preview generation failed.");
                }
            } catch (error) {
                clearInterval(interval);
                console.error(error);
                setPreviewError("Preview is not available yet.");
            }
        }, 3000);

        return interval;
    }, [loadGuestPreview, hydrateFormFromPreview, refreshPreview]);

    const submitGeneration = useCallback(async (payload) => {
        if (currentUser) {
            const res = await createAndQueueProject(payload);

            setProjectId(res?.projectId || "");
            setPreviewSessionId("");
            setOwnershipMode("account");
            setStatus(res?.status || "Submitted");
            setPreviewData(null);
            setLiveUrl("");
            resetHydrationState();
            clearPersistedPreviewSessionId();

            if (res?.projectId) {
                pollProjectStatus(res.projectId);
            }

            return;
        }

        const res = await createAndQueuePreviewSession(payload);

        if (!res?.previewSessionId) {
            throw new Error("Preview session creation failed.");
        }

        setPreviewSessionId(res.previewSessionId);
        setProjectId("");
        setOwnershipMode("guest");
        setStatus(res?.status || "Submitted");
        setPreviewData(null);
        setLiveUrl("");
        resetHydrationState();

        persistPreviewSessionId(res.previewSessionId);
        pollPreviewSessionStatus(res.previewSessionId);
    }, [
        currentUser,
        pollProjectStatus,
        pollPreviewSessionStatus,
        persistPreviewSessionId,
        clearPersistedPreviewSessionId,
        resetHydrationState
    ]);

    const hydrateClaimedProject = useCallback(async (claimedProjectId) => {
        if (!claimedProjectId) return;

        setProjectId(claimedProjectId);
        setPreviewSessionId("");
        setOwnershipMode("account");
        setStep(3);
        clearPersistedPreviewSessionId();

        const projectPreview = await loadProjectPreview(claimedProjectId);

        setPreviewData(projectPreview || null);
        setStatus(projectPreview?.status || "Generated");
        setLiveUrl(projectPreview?.liveUrl || "");

        if (projectPreview) {
            hydrateFormFromPreview(projectPreview);
        }

        setSelectedVariant("A");
        setPreviewRefreshKey(Date.now());
    }, [
        clearPersistedPreviewSessionId,
        hydrateFormFromPreview,
        loadProjectPreview
    ]);

    useEffect(() => {
        let mounted = true;

        const loadCurrentUser = async () => {
            const isAuthReturn = queryParams.get("authReturn") === "1";

            try {
                if (isAuthReturn) {
                    await new Promise((resolve) => setTimeout(resolve, 250));
                }

                const me = await getCurrentUser();

                if (!mounted) return;

                setCurrentUser(me || null);
                setOwnershipMode(me ? "account" : "guest");

                if (me && isAuthReturn) {
                    const url = new URL(window.location.href);
                    url.searchParams.delete("authReturn");
                    window.history.replaceState({}, "", url.toString());
                }
            } catch {
                if (!mounted) return;

                setCurrentUser(null);
                setOwnershipMode("guest");
            } finally {
                if (mounted) setAuthChecked(true);
            }
        };

        loadCurrentUser();

        return () => {
            mounted = false;
        };
    }, [queryParams]);

    useEffect(() => {
        const loadThemes = async () => {
            try {
                const res = await getThemes();

                if (Array.isArray(res) && res.length > 0) {
                    setThemes(res);
                }
            } catch {
                setThemes(fallbackThemes);
            }
        };

        loadThemes();
    }, []);

    useEffect(() => {
        if (!authChecked) return;

        const restorePreview = async () => {
            const pendingPreviewId = getPendingPreviewSessionId();

            if (!pendingPreviewId) {
                setPreviewRestoreChecked(true);
                return;
            }

            if (projectId || previewSessionId || previewData) {
                setPreviewRestoreChecked(true);
                return;
            }

            try {
                setLoading(true);
                setPreviewError("");

                const preview = await getPreviewSession(pendingPreviewId);
                if (!preview) return;

                setPreviewSessionId(pendingPreviewId);
                setPreviewData(preview);
                setStatus(preview?.status || "");
                setOwnershipMode(currentUser ? "account" : "guest");
                setStep(3);

                hydrateFormFromPreview(preview);
                persistPreviewSessionId(pendingPreviewId);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
                setPreviewRestoreChecked(true);
            }
        };

        restorePreview();
    }, [
        authChecked,
        currentUser,
        projectId,
        previewSessionId,
        previewData,
        getPendingPreviewSessionId,
        hydrateFormFromPreview,
        persistPreviewSessionId
    ]);

    useEffect(() => {
        if (!authChecked) return;
        if (!previewSeed?.autoGenerate) return;
        if (autoPreviewTriggered) return;

        const pendingPreviewId = getPendingPreviewSessionId();

        if (pendingPreviewId || previewSessionId || projectId || previewData) {
            return;
        }

        const hasBusinessName = !!previewSeed.businessName?.trim();
        const hasIndustry = !!previewSeed.industry?.trim();

        if (!hasBusinessName || !hasIndustry) return;

        setAutoPreviewTriggered(true);

        const timer = setTimeout(async () => {
            try {
                setStep(3);
                setLoading(true);
                setPreviewError("");
                setPreviewData(null);
                setLiveUrl("");
                setProjectId("");
                setPreviewSessionId("");
                setStatus("Preparing...");

                const payload = {
                    ...buildCreatePayload(),
                    businessName: previewSeed.businessName,
                    industry: previewSeed.industry,
                    description:
                        form.description?.trim() ||
                        `${previewSeed.businessName} is a premium ${previewSeed.industry.toLowerCase()} business looking for a modern, high-converting website experience.`,
                    goalOfWebsite: form.goalOfWebsite || "Generate Leads",
                    brandStyle: form.brandStyle || "Premium",
                    tone: form.tone || "Professional"
                };

                await submitGeneration(payload);
            } catch (error) {
                console.error(error);
                setPreviewError("Failed to auto-start preview.");
            } finally {
                setLoading(false);
            }
        }, 600);

        return () => clearTimeout(timer);
    }, [
        authChecked,
        previewRestoreChecked,
        previewSeed,
        autoPreviewTriggered,
        buildCreatePayload,
        submitGeneration,
        getPendingPreviewSessionId,
        previewSessionId,
        projectId,
        previewData,
        form.description,
        form.goalOfWebsite,
        form.brandStyle,
        form.tone
    ]);

    useEffect(() => {
        if (!previewData) return;
        hydrateFormFromPreview(previewData);
    }, [previewData, hydrateFormFromPreview]);

    useEffect(() => {
        if (!lastAutofillSignature) return;

        const currentSignature = buildAutofillSignature();
        setNeedsSuggestionRefresh(currentSignature !== lastAutofillSignature);
    }, [
        form.businessName,
        form.industry,
        form.subIndustry,
        form.description,
        form.coreServices,
        form.projectTypes,
        form.goalOfWebsite,
        form.primaryAudience,
        form.offeredItems,
        lastAutofillSignature,
        buildAutofillSignature
    ]);

    useEffect(() => {
        if (previewData || projectId || previewSessionId) return;

        const essentialsReady =
            !!form.businessName?.trim() &&
            !!form.industry?.trim() &&
            !!form.description?.trim() &&
            !!form.goalOfWebsite?.trim();

        if (!essentialsReady) {
            setAutoAutofillTriggered(false);
            return;
        }

        if (autofilling || autoAutofillTriggered) return;

        const timer = setTimeout(() => {
            setAutoAutofillTriggered(true);
            handleAutofill();
        }, 900);

        return () => clearTimeout(timer);
    }, [
        form.businessName,
        form.industry,
        form.description,
        form.goalOfWebsite,
        autofilling,
        autoAutofillTriggered,
        handleAutofill,
        previewData,
        projectId,
        previewSessionId
    ]);

    useEffect(() => {
        if (!authChecked) return;
        if (!currentUser) return;
        if (projectId) return;
        if (claimingPreview) return;
        if (autoClaimAttemptedRef.current) return;

        const pendingId = previewSessionId || getPendingPreviewSessionId();

        if (!pendingId) return;

        autoClaimAttemptedRef.current = true;

        const runAutoClaim = async () => {
            try {
                setClaimingPreview(true);
                setLoading(true);
                setOwnershipMode("account");
                setPreviewSessionId(pendingId);
                setStep(3);
                setStatus("Saving preview...");

                const generated = await isPreviewSessionGenerated(pendingId);

                if (!generated) {
                    setStatus("Generating");
                    pollPreviewSessionStatus(pendingId);
                    autoClaimAttemptedRef.current = false;
                    return;
                }

                const result = await claimPreviewSession(pendingId);

                if (result?.projectId) {
                    await hydrateClaimedProject(result.projectId);
                    setStatus("Generated");
                    toast.success("Your preview has been linked to your account 🚀");
                } else {
                    autoClaimAttemptedRef.current = false;
                }
            } catch (error) {
                console.error(error);
                autoClaimAttemptedRef.current = false;
            } finally {
                setClaimingPreview(false);
                setLoading(false);
            }
        };

        runAutoClaim();
    }, [
        authChecked,
        currentUser,
        previewSessionId,
        projectId,
        claimingPreview,
        getPendingPreviewSessionId,
        hydrateClaimedProject,
        pollPreviewSessionStatus
    ]);

    const ensureLoggedInBeforeProtectedAction = async () => {
        try {
            const me = await getCurrentUser();
            setCurrentUser(me || null);
            setOwnershipMode(me ? "account" : "guest");
            return true;
        } catch {
            redirectToLogin(window.location.href);
            return false;
        }
    };

    const handleSave = async () => {
        if (claimingPreview) return;

        if (currentUser && previewSessionId && !projectId) {
            await handleClaimPreview();
            return;
        }

        if (projectId) {
            toast("This website is already saved. Use Regenerate or Update Live Site from CRM.");
            return;
        }

        try {
            setLoading(true);
            setPreviewError("");
            setPreviewData(null);
            setLiveUrl("");
            setProjectId("");
            setPreviewSessionId("");
            setStatus("");
            resetHydrationState();

            const payload = buildCreatePayload();
            await submitGeneration(payload);
        } catch (error) {
            console.error(error);
            toast.error("Failed to start generation.");
        } finally {
            setLoading(false);
        }
    };

    const handleMobileChatGenerate = async (finalPatch = {}) => {
        if (claimingPreview) return;

        if (currentUser && previewSessionId && !projectId) {
            await handleClaimPreview();
            return;
        }

        if (projectId) {
            toast("This website is already saved. Use Regenerate or Update Live Site from CRM.");
            return;
        }

        try {
            setLoading(true);
            setPreviewError("");
            setPreviewData(null);
            setLiveUrl("");
            setProjectId("");
            setPreviewSessionId("");
            setStatus("");
            resetHydrationState();

            const basePayload = buildCreatePayload();

            const payload = {
                ...basePayload,
                ...finalPatch,

                businessName:
                    finalPatch.businessName ||
                    basePayload.businessName ||
                    "My Business",

                industry:
                    finalPatch.industry ||
                    basePayload.industry ||
                    "Business",

                subIndustry:
                    finalPatch.subIndustry ||
                    basePayload.subIndustry ||
                    finalPatch.industry ||
                    basePayload.industry ||
                    "Business",

                description:
                    finalPatch.description ||
                    basePayload.description ||
                    "A professional business website generated by Shriansh Nexus.",

                goalOfWebsite:
                    finalPatch.goalOfWebsite ||
                    basePayload.goalOfWebsite ||
                    finalPatch.callToActionType ||
                    "Generate Leads",

                callToActionType:
                    finalPatch.callToActionType ||
                    basePayload.callToActionType ||
                    "Contact Us",

                brandStyle:
                    finalPatch.brandStyle ||
                    basePayload.brandStyle ||
                    "Premium",

                tone:
                    finalPatch.tone ||
                    basePayload.tone ||
                    "Professional",

                primaryAudience:
                    finalPatch.primaryAudience ||
                    basePayload.primaryAudience ||
                    "Local customers",

                coreServices:
                    finalPatch.coreServices ||
                    basePayload.coreServices ||
                    "Services",

                pages:
                    Array.isArray(finalPatch.pages) && finalPatch.pages.length > 0
                        ? finalPatch.pages
                        : Array.isArray(basePayload.pages) && basePayload.pages.length > 0
                            ? basePayload.pages
                            : ["Home", "About", "Services", "Contact"]
            };

            await submitGeneration(payload);
        } catch (error) {
            console.error(error);
            toast.error("Failed to start generation.");
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        try {
            setLoading(true);
            setPreviewError("");

            let res = null;

            if (projectId) {
                res = await loadProjectPreview(projectId);
            } else if (previewSessionId) {
                res = await loadGuestPreview(previewSessionId);
            } else {
                setPreviewError("Generate preview first.");
                return;
            }

            if (!res) {
                setPreviewError("Preview is not available yet.");
                return;
            }

            const previewStatus = res?.status || "";

            if (previewStatus !== "Generated" && !res?.content && !res?.html) {
                setPreviewError("Preview is not available yet.");
            }
        } catch (error) {
            console.error(error);
            setPreviewError("Preview is not available yet.");
        } finally {
            setLoading(false);
        }
    };

    const isPreviewSessionGenerated = async (id) => {
        if (!id) return false;

        try {
            const statusRes = await getPreviewSessionStatus(id);
            const currentStatus = statusRes?.status || "";

            setStatus(currentStatus);

            return currentStatus === "Generated";
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const handleClaimPreview = async () => {
        if (!previewSessionId) {
            toast.error("Preview session not found.");
            return;
        }

        if (claimingPreview) return;

        const loadingToast = toast.loading("Saving your preview to CRM...");

        try {
            setClaimingPreview(true);
            setLoading(true);
            setPreviewError("");

            const me = await getCurrentUser().catch(() => null);

            if (!me) {
                toast.dismiss(loadingToast);
                redirectToSignup(window.location.href);
                return;
            }

            setCurrentUser(me);
            setOwnershipMode("account");

            const generated = await isPreviewSessionGenerated(previewSessionId);

            if (!generated) {
                toast("Preview is still generating. We will save it once ready.", {
                    id: loadingToast
                });

                pollPreviewSessionStatus(previewSessionId);
                return;
            }

            const result = await claimPreviewSession(previewSessionId);

            if (result?.projectId) {
                await hydrateClaimedProject(result.projectId);

                setStatus("Generated");
                setPreviewError("");
                setStep(3);

                toast.success("Project saved successfully 🚀", {
                    id: loadingToast
                });
            } else {
                toast.error("Project could not be saved.", {
                    id: loadingToast
                });
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong while saving preview.", {
                id: loadingToast
            });
        } finally {
            setClaimingPreview(false);
            setLoading(false);
        }
    };

    const pickAutoFixMode = (preview) => {
        const suggestions = Array.isArray(preview?.qualitySuggestions)
            ? preview.qualitySuggestions.map((x) => String(x).toLowerCase())
            : [];

        const summary = String(preview?.qualitySummary || "").toLowerCase();
        const combined = [...suggestions, summary].join(" ");

        if (combined.includes("cta") || combined.includes("call to action")) return "cta";
        if (combined.includes("industry") || combined.includes("native")) return "industry";
        if (combined.includes("trust") || combined.includes("credibility")) return "trust";
        if (combined.includes("conversion") || combined.includes("clarity")) return "conversion";

        return "realism";
    };

    const handleAutoFix = async () => {
        if (!previewData) {
            toast.error("Preview quality data is not available yet.");
            return;
        }

        const mode = pickAutoFixMode(previewData);
        await handleRegenerate(mode);
    };

    const handlePublish = async () => {
        const allowed = await ensureLoggedInBeforeProtectedAction();
        if (!allowed) return;

        if (!projectId) {
            toast.error("Please create or claim a saved project before publishing.");
            return;
        }

        if (isPublished) {
            toast("Website is already published.");
            return;
        }

        const score = previewData?.qualityScore ?? null;

        if (score != null) {
            if (score < 70) {
                const proceed = window.confirm(
                    "This website is below the recommended publish quality threshold. It is better to improve it before publishing. Do you still want to continue?"
                );

                if (!proceed) return;
            } else if (score < 85) {
                const proceed = window.confirm(
                    "This website is publishable, but a final review is recommended. Do you want to continue with publishing?"
                );

                if (!proceed) return;
            }
        }

        try {
            setPublishing(true);
            setPreviewError("");

            const res = await publishProject(projectId);

            if (res?.liveUrl) {
                setLiveUrl(res.liveUrl || "");
                setStatus("Published");

                const latestPreview = await loadProjectPreview(projectId);

                if (latestPreview?.liveUrl) {
                    setLiveUrl(latestPreview.liveUrl);
                }

                setSelectedVariant("A");
                setPreviewRefreshKey(Date.now());

                toast.success("Website published successfully.");
            } else {
                toast.error("Publish failed.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to publish website.");
        } finally {
            setPublishing(false);
        }
    };

    const handleRegenerate = async (improvementMode = null) => {
        const allowed = await ensureLoggedInBeforeProtectedAction();
        if (!allowed) return;

        if (!projectId) {
            toast.error("Please create or claim a saved project before regenerating.");
            return;
        }

        try {
            setRegenerating(true);
            setPreviewError("");
            setStep(3);

            const payload = improvementMode ? { improvementMode } : {};
            const res = await regenerateProject(projectId, payload);

            if (res) {
                setStatus("Submitted");
                resetHydrationState();
                setPreviewData(null);
                setLiveUrl("");

                toast.success(
                    improvementMode
                        ? `Regeneration started: ${improvementMode}`
                        : "Regeneration started."
                );

                pollProjectStatus(projectId);
            } else {
                toast.error("Regeneration failed.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to regenerate website.");
        } finally {
            setRegenerating(false);
        }
    };

    const handleOpenLivePreview = () => {
        if (!projectId) return;

        window.open(
            `${API_BASE_URL}/api/website/${projectId}/preview-document?v=${selectedVariant}&r=${Date.now()}`,
            "_blank"
        );
    };

    const normalizedStatus = String(status || "").toLowerCase();

    const isPublished =
        !!liveUrl &&
        (
            normalizedStatus === "published" ||
            normalizedStatus === "generated" ||
            !!previewData?.publishedAtUtc ||
            !!previewData?.liveUrl
        );

    const qualityScore = Number(
        previewData?.qualityScore ??
        previewData?.productionReadyScore ??
        0
    );

    const isHighQuality =
        qualityScore >= 85 ||
        String(previewData?.qualityStatus || "").toLowerCase() === "excellent" ||
        String(previewData?.qualityStatus || "").toLowerCase() === "pass";

    const shouldShowPublishGuardrail =
        !!previewData &&
        !isPublished &&
        qualityScore > 0 &&
        qualityScore < 85;

    const canGenerate =
        !!form.businessName &&
        !!form.industry &&
        !projectId &&
        !previewSessionId &&
        !loading &&
        !claimingPreview &&
        !publishing &&
        !regenerating;

    const canPreview =
        (!!projectId || !!previewSessionId) &&
        (status === "Generated" || status === "Published") &&
        !loading;

    const canPublish =
        !!projectId &&
        status === "Generated" &&
        !isPublished &&
        !publishing;

    const canRegenerate =
        !!projectId &&
        !regenerating &&
        !loading;

    const showAssistant =
        step === 1 &&
        generatorMode === "assistant" &&
        !projectId &&
        !previewSessionId &&
        !previewData;

    const previewIsGenerating =
        isProjectGenerating({ status }) ||
        loading ||
        status === "Submitted" ||
        status === "Preparing..." ||
        status === "Generating";

    const iframePreviewSrc = previewDocumentUrl;

    const isPreviewReady =
        Boolean(iframePreviewSrc) &&
        Boolean(previewData) &&
        !previewIsGenerating &&
        (status === "Generated" || status === "Published");

    const shouldShowMobileComposer =
        isMobileGenerator &&
        showAssistant &&
        !mobileComposerStarted &&
        !projectId &&
        !previewSessionId &&
        !previewData &&
        !loading &&
        !autofilling;

    const shouldShowMobileChatFlow =
        isMobileGenerator &&
        mobileChatStarted &&
        mobileComposerStarted &&
        !projectId &&
        !previewSessionId &&
        !previewData &&
        !loading &&
        !autofilling;

    if (shouldShowMobileComposer) {
        return (
            <main className="mgc-page">
                <MobileGeneratorComposer
                    form={form}
                    updateField={updateField}
                    onStart={() => {
                        setMobileComposerStarted(true);
                        setMobileChatStarted(true);
                        setGeneratorMode("assistant");
                        setFormMode("fast");
                        setShowAiSummary(false);
                        setShowAdvanced(false);
                        setShowOfferings(false);
                        setStep(1);
                    }}
                />
            </main>
        );
    }

    if (shouldShowMobileChatFlow) {
        return (
            <MobileGeneratorChatFlow
                form={form}
                updateField={updateField}
                generating={loading}
                canGenerate={true}
                onBack={() => {
                    setMobileChatStarted(false);
                    setMobileComposerStarted(false);
                }}
                onGenerate={(finalPatch) => {
                    setMobileChatStarted(false);
                    setMobileComposerStarted(false);
                    handleMobileChatGenerate(finalPatch);
                }}
            />
        );
    }

    return (
        <div className="landing-page">
            <div className="landing-bg">
                <div className="landing-orb landing-orb-1" />
                <div className="landing-orb landing-orb-2" />
                <div className="landing-orb landing-orb-3" />
                <div className="landing-grid" />
            </div>

            <header className="landing-header">
                <div className="landing-container landing-header-inner">
                    <div className="landing-brand">
                        <div className="landing-brand-logo">
                            <Sparkles size={20} />
                        </div>

                        <div>
                            <div className="landing-brand-title">Shriansh Nexus</div>
                            <div className="landing-brand-subtitle">Built by Shriansh AI</div>
                        </div>
                    </div>

                    <nav className="landing-nav">
                        <Link to="/">Home</Link>

                        {currentUser ? (
                            <>
                                <a
                                    href="https://admin.shrianshnexus.in/dashboard"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Dashboard
                                </a>

                                {projectId && (
                                    <a
                                        href={`https://admin.shrianshnexus.in/crm/projects/${projectId}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        CRM
                                    </a>
                                )}
                            </>
                        ) : (
                            <>
                                <a href={loginUrl}>Login</a>
                                <a href={signupUrl}>Start Free</a>
                            </>
                        )}
                    </nav>

                    <div className="landing-header-actions">
                        <Link className="landing-btn landing-btn-ghost" to="/">
                            Back to Home
                        </Link>

                        {currentUser ? (
                            <a
                                className="landing-btn landing-btn-primary"
                                href="https://admin.shrianshnexus.in/dashboard"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Go to Dashboard
                                <ArrowRight size={16} />
                            </a>
                        ) : (
                            <a
                                className="landing-btn landing-btn-primary"
                                href={signupUrl}
                            >
                                Create Account
                                <ArrowRight size={16} />
                            </a>
                        )}
                    </div>
                </div>
            </header>

            <main className="landing-section" style={{ paddingTop: 48 }}>
                <div className="landing-container">
                    <div className="landing-eyebrow left">
                        <Sparkles size={16} />
                        <span>AI Website Generator Workspace</span>
                    </div>

                    <h1 className="landing-left-title" style={{ marginBottom: 12 }}>
                        Build, preview, and publish with the same premium Nexus experience.
                    </h1>

                    <p className="landing-left-subtitle" style={{ maxWidth: 900, marginBottom: 28 }}>
                        Fill your business details, let AI enrich the content, choose a theme, then
                        save, preview, and publish — all inside the Shriansh Nexus generation flow.
                    </p>

                    <div className={`wg-page ${shouldShowMobileComposer ? "wg-shell-composer-active" : ""}`}>
                        <div className={`wg-shell ${shouldShowMobileComposer ? "wg-shell-composer-active" : ""}`}>
                            <div className="wg-header-compact">
                                <div className="wg-header-compact-inner">
                                    <div className="wg-header-left">
                                        <div className="wg-badge">Shriansh Nexus</div>

                                        <h2 className="wg-header-title">
                                            AI Website Generator
                                        </h2>

                                        <p className="wg-header-subtitle">
                                            Build faster with AI suggestions, instant preview, and a guided generation flow.
                                        </p>
                                    </div>

                                    <div className="wg-header-right">
                                        <span className="wg-header-step">
                                            {showAssistant ? "AI Assistant" : `Step ${step} of 3`}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {!previewData && (
                                <GeneratorInstantPreview
                                    form={form}
                                    previewSeed={previewSeed}
                                />
                            )}

                            <div className="wg-preview-toggle-card">
                                <div className="wg-preview-toggle-copy">
                                    <strong>Preview Snapshot</strong>
                                    <span>
                                        {showHeroPreview
                                            ? "Hide compact hero snapshot"
                                            : "Show compact hero snapshot"}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className="wg-secondary-btn"
                                    onClick={() => setShowHeroPreview((prev) => !prev)}
                                >
                                    {showHeroPreview ? "Hide" : "Show"}
                                </button>
                            </div>

                            {(showHeroPreview || formMode === "detailed") && (
                                <div className="wg-hero-preview-collapsed">
                                    <GeneratorHeroPreview form={form} previewSeed={previewSeed} />
                                </div>
                            )}

                            <StepProgress step={step} />

                            <div className="wg-card">
                                {showAssistant && (
                                    <AiGeneratorAssistant
                                        form={form}
                                        updateField={updateField}
                                        onAutofill={handleAutofill}
                                        autofilling={autofilling}
                                        onUseManual={() => {
                                            setGeneratorMode("manual");
                                            setFormMode("fast");
                                        }}
                                        onComplete={() => {
                                            setGeneratorMode("manual");
                                            setFormMode("fast");
                                            setShowAiSummary(true);
                                            setShowAdvanced(false);
                                            setShowOfferings(false);
                                            setStep(2);
                                            toast.success("AI plan prepared. Review style and generate preview.");
                                        }}
                                    />
                                )}

                                {step === 1 && !showAssistant && (
                                    <>
                                        {autofilling && (
                                            <GeneratorThinkingPanel
                                                mode="autofill"
                                                active={autofilling}
                                            />
                                        )}

                                        {!autofilling && needsSuggestionRefresh && (
                                            <SuggestionsRefreshNotice
                                                onRefresh={handleAutofill}
                                                refreshing={autofilling}
                                            />
                                        )}

                                        {!autofilling && (
                                            <>
                                                <div className="wg-ai-summary-toggle">
                                                    <div className="wg-ai-summary-toggle-copy">
                                                        <strong>AI Strategy Summary</strong>
                                                        <span>
                                                            {showAiSummary || formMode === "detailed"
                                                                ? "Hide detailed AI understanding"
                                                                : "Show what AI understood about your business"}
                                                        </span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="wg-secondary-btn"
                                                        onClick={() => setShowAiSummary((prev) => !prev)}
                                                    >
                                                        {(showAiSummary || formMode === "detailed") ? "Hide" : "Expand"}
                                                    </button>
                                                </div>

                                                {(showAiSummary || formMode === "detailed") && (
                                                    <SmartSuggestionsSummaryPanel form={form} />
                                                )}
                                            </>
                                        )}

                                        <BusinessDetailsStep
                                            form={form}
                                            updateField={updateField}
                                            onAutofill={handleAutofill}
                                            autofilling={autofilling}
                                            mode={formMode}
                                            setMode={setFormMode}
                                            showOfferings={showOfferings}
                                            setShowOfferings={setShowOfferings}
                                            showAdvanced={showAdvanced}
                                            setShowAdvanced={setShowAdvanced}
                                            fieldSourceMap={fieldSourceMap}
                                            lockedAiFields={lockedAiFields}
                                            onToggleAiFieldLock={toggleAiFieldLock}
                                        />
                                    </>
                                )}

                                {step === 2 && (
                                    <ThemeSelectionStep
                                        themes={themes}
                                        form={form}
                                        updateField={updateField}
                                    />
                                )}

                                {step === 3 && (
                                    <ReviewStep
                                        form={form}
                                        projectId={projectId}
                                        status={status}
                                        liveUrl={liveUrl}
                                    />
                                )}
                            </div>

                            {step === 1 && !showAssistant && (
                                <div className="wg-sticky-nextbar">
                                    <div className="wg-sticky-nextbar-inner">
                                        <div className="wg-sticky-nextbar-copy">
                                            <strong>Step 1 of 3</strong>
                                            <span>Fill essentials, then continue to theme selection</span>
                                        </div>

                                        <button
                                            type="button"
                                            className="wg-btn primary"
                                            onClick={() => setStep(2)}
                                            disabled={!form.businessName || !form.industry}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step !== 1 && (
                                <ActionBar
                                    step={step}
                                    loading={loading}
                                    publishing={publishing}
                                    regenerating={regenerating}
                                    canGenerate={canGenerate}
                                    canPreview={canPreview}
                                    canPublish={canPublish}
                                    canRegenerate={canRegenerate}
                                    previewError={previewError}
                                    onBack={() => setStep((s) => Math.max(1, s - 1))}
                                    onNext={() => setStep((s) => Math.min(3, s + 1))}
                                    onSave={handleSave}
                                    onPreview={handlePreview}
                                    onPublish={handlePublish}
                                    onRegenerate={handleRegenerate}
                                />
                            )}

                            {(loading || status === "Submitted" || status === "Preparing..." || status === "Generating") && !previewData && (
                                <>
                                    <GeneratorThinkingPanel
                                        mode="generation"
                                        status={status}
                                        active={loading || status === "Submitted" || status === "Preparing..." || status === "Generating"}
                                    />

                                    <GeneratorLoadingCard status={status} />
                                </>
                            )}

                            {previewError && (
                                <div className="wg-preview-status wg-preview-status-error">
                                    {previewError}
                                </div>
                            )}

                            {previewData && (status === "Generated" || status === "Published") && (
                                <MobilePreviewReadyCard
                                    ownershipMode={ownershipMode}
                                    projectId={projectId}
                                    liveUrl={liveUrl}
                                    isPublished={isPublished}
                                    saving={claimingPreview}
                                    onSave={handleClaimPreview}
                                    onLogin={() => redirectToLogin(window.location.href)}
                                />
                            )}

                            {previewData && status === "Generated" && ownershipMode === "guest" && (
                                <div className="wg-crm-bridge-card">
                                    <div className="wg-crm-bridge-copy">
                                        <div className="wg-badge">
                                            <Sparkles size={16} />
                                            <span>Preview Ready</span>
                                        </div>

                                        <h3>Your website + CRM system is ready</h3>

                                        <p>
                                            This is not just a website. Nexus has prepared your business system —
                                            including editable modules, AI content, and deployment pipeline.
                                        </p>

                                        <div className="wg-crm-preview-points">
                                            <span>✔ Editable CRM modules</span>
                                            <span>✔ AI content + images</span>
                                            <span>✔ Deployment & variants</span>
                                        </div>
                                    </div>

                                    <div className="wg-crm-bridge-actions">
                                        <button
                                            type="button"
                                            className="wg-primary-btn"
                                            onClick={handleClaimPreview}
                                        >
                                            Save & Open CRM →
                                        </button>

                                        <button
                                            type="button"
                                            className="wg-secondary-btn"
                                            onClick={() => redirectToLogin(window.location.href)}
                                        >
                                            Login to Continue
                                        </button>
                                    </div>
                                </div>
                            )}

                            {previewData && (status === "Generated" || status === "Published") && ownershipMode === "account" && (
                                <>
                                    <div className="wg-crm-bridge-card">
                                        <div className="wg-crm-bridge-copy">
                                            <h3>Your system is ready inside CRM</h3>

                                            <p>
                                                Manage your business data, update content, control modules,
                                                and instantly redeploy your website — all from your Nexus dashboard.
                                            </p>
                                        </div>

                                        <div className="wg-crm-bridge-actions">
                                            {projectId && (
                                                <>
                                                    <a
                                                        className="wg-primary-btn"
                                                        href={`https://admin.shrianshnexus.in/crm/projects/${projectId}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Open in CRM
                                                    </a>

                                                    <a
                                                        className="wg-secondary-btn"
                                                        href="https://admin.shrianshnexus.in/dashboard"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Continue to Dashboard
                                                    </a>
                                                </>
                                            )}

                                            {isPublished && liveUrl && (
                                                <a
                                                    className="wg-secondary-btn"
                                                    href={liveUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View Published Site
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <QualityCard
                                        preview={{
                                            ...previewData,
                                            qualityStatus: isHighQuality ? "Excellent" : previewData?.qualityStatus
                                        }}
                                    />

                                    {shouldShowPublishGuardrail && (
                                        <PublishGuardrailCard preview={previewData} />
                                    )}

                                    <div className="wg-autofix-card">
                                        <div className="wg-autofix-copy">
                                            <h3>One-click Improve</h3>
                                            <p>
                                                Let Shriansh Nexus automatically pick the strongest improvement
                                                area from the quality analysis and regenerate a better version.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="wg-primary-btn"
                                            disabled={regenerating}
                                            onClick={handleAutoFix}
                                        >
                                            {regenerating ? "Improving..." : "Improve Website Automatically"}
                                        </button>
                                    </div>

                                    <SmartRegenerateActions
                                        preview={previewData}
                                        loading={regenerating}
                                        onRegenerate={handleRegenerate}
                                    />
                                </>
                            )}

                            {(projectId || previewSessionId) && previewData && (
                                <div className="embedded-preview">
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 12,
                                            marginBottom: 16,
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        {projectId && (
                                            <button
                                                type="button"
                                                className="wg-secondary-btn"
                                                onClick={handleOpenLivePreview}
                                            >
                                                Open Live Preview
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            className="landing-btn landing-btn-secondary"
                                            onClick={() => {
                                                setSelectedVariant("A");
                                                refreshPreview();
                                            }}
                                        >
                                            Variant A
                                        </button>

                                        <button
                                            type="button"
                                            className="landing-btn landing-btn-secondary"
                                            onClick={() => {
                                                setSelectedVariant("B");
                                                refreshPreview();
                                            }}
                                        >
                                            Variant B
                                        </button>
                                    </div>

                                    <div className="snx-preview-frame-wrap">
                                        {!isPreviewReady ? (
                                            <div className="snx-preview-generating-state">
                                                <div className="snx-preview-loader" />
                                                <h3>Your website is being generated</h3>
                                                <p>
                                                    We are preparing your preview. This usually takes a few moments.
                                                </p>
                                            </div>
                                        ) : (
                                            <iframe
                                                key={`${selectedVariant}-${previewRefreshKey}`}
                                                title="Website preview"
                                                src={iframePreviewSrc}
                                                className="snx-preview-iframe"
                                                loading="lazy"
                                            />
                                        )}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <GeneratorFooter />
        </div>
    );
}